import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { Dialog } from '@headlessui/react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  Compass,
  Rocket,
  LifeBuoy,
  ClipboardCheck,
  LayoutDashboard,
  Users,
  Map,
  Target,
  Filter,
  Handshake,
  UserCheck,
  FileSignature,
  ListChecks,
  TrendingUp,
  Activity,
  Layers,
  RefreshCw,
  Calendar,
  Sparkles,
  Brain,
  AlertTriangle,
  Zap,
  Gift,
  Heart,
  Building2,
  ArrowRight,
} from 'lucide-react';

export interface PlanDeMatchIntegrationAgenticAIModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional CE controls; the IA Agentic presentation remains the single source. */
  workshopFooter?: ReactNode;
}

/** @deprecated Utiliser PlanDeMatchIntegrationAgenticAIModalProps */
export type RaciDeploiementAgentiqueModalProps = PlanDeMatchIntegrationAgenticAIModalProps;

type MainTabId = 'vue' | 'raci' | 'operationnel' | 'vente';

const MAIN_TABS: { id: MainTabId; label: string; icon: typeof LayoutDashboard }[] = [
  { id: 'vue', label: "Vue d'ensemble", icon: LayoutDashboard },
  { id: 'raci', label: 'RACI', icon: Users },
  { id: 'operationnel', label: 'Plan de Match Opérationnel', icon: Map },
  { id: 'vente', label: 'Vente', icon: Handshake },
];

type RaciCode = 'R' | 'A' | 'A/R' | 'C' | 'I';

type RoleKey = 'client' | 'compte' | 'cp' | 'prog' | 'dti' | 'ti' | 'cloud' | 'achat';

const ROLES: { key: RoleKey; label: string; who: string }[] = [
  { key: 'client', label: 'Dir. TI client', who: 'Client' },
  { key: 'compte', label: 'Directeurs de compte', who: 'BZ · Ventes' },
  { key: 'cp', label: 'Chargé de projet', who: 'BZ · Eric Durand' },
  { key: 'prog', label: 'Programmation', who: 'BZ · Équipe programmation' },
  { key: 'dti', label: 'Directeurs TI', who: 'BZ · Direction TI' },
  { key: 'ti', label: 'Support TI', who: 'BZ · Infrastructure' },
  { key: 'cloud', label: 'Infonuagique', who: 'BZ · Équipe du cloud' },
  { key: 'achat', label: 'Équipe d\'achat', who: 'BZ · Achats' },
];

const RACI_LEGEND: { code: RaciCode; title: string; desc: string }[] = [
  { code: 'R', title: 'Responsable', desc: 'Exécute concrètement le travail.' },
  { code: 'A', title: 'Approbateur', desc: 'Redevable, tranche et approuve.' },
  { code: 'C', title: 'Consulté', desc: 'Donne son input avant la décision.' },
  { code: 'I', title: 'Informé', desc: 'Tenu au courant après coup.' },
];

const RACI_BADGE: Record<RaciCode, string> = {
  R: 'bg-sky-500 text-white shadow-sky-500/40',
  A: 'bg-emerald-500 text-white shadow-emerald-500/40',
  'A/R': 'bg-gradient-to-br from-emerald-500 to-sky-500 text-white shadow-emerald-500/40',
  C: 'bg-amber-400 text-slate-900 shadow-amber-400/40',
  I: 'bg-violet-500 text-white shadow-violet-500/40',
};

type Composante = {
  name: string;
  description: string;
  raci: Partial<Record<RoleKey, RaciCode>>;
  /** Nom affiché sous la pastille RACI (ex. Eric Durand pour le A/R) */
  assignees?: Partial<Record<RoleKey, string>>;
};

const RACI_COLUMN_ORDER: Record<RaciCode, number> = { I: 0, C: 1, R: 2, A: 3, 'A/R': 4 };

function activeRolesFor(composante: Composante) {
  return ROLES.filter((role) => composante.raci[role.key] != null).sort(
    (a, b) =>
      (RACI_COLUMN_ORDER[composante.raci[a.key]!] ?? 0) -
      (RACI_COLUMN_ORDER[composante.raci[b.key]!] ?? 0)
  );
}

type Phase = {
  id: string;
  step: string;
  title: string;
  subtitle: string;
  icon: typeof Compass;
  tone: {
    text: string;
    dot: string;
    ring: string;
    chip: string;
    glow: string;
  };
  composantes: Composante[];
};

const PHASES: Phase[] = [
  {
    id: 'etape0-signature',
    step: 'Étape 0',
    title: 'Signature & cadrage client',
    subtitle: "Avant l'exécution : qualifier, cadrer, regrouper le contrat et faire signer.",
    icon: FileSignature,
    tone: {
      text: 'text-emerald-300',
      dot: 'bg-emerald-400',
      ring: 'ring-emerald-400/40',
      chip: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/30',
      glow: 'bg-emerald-500/20',
    },
    composantes: [
      {
        name: 'A. Qualification client — True Friends / Butterflies / Barnacles / Strangers',
        description:
          "Qualifier le compte selon le potentiel et la force de la relation pour décider si BZ accélère, recadre, limite l'effort ou refuse poliment avant de consommer de la capacité de livraison.",
        raci: { compte: 'R', cp: 'A', dti: 'C' },
        assignees: { compte: 'Directeurs de compte', cp: 'Eric Durand', dti: 'Directeurs TI' },
      },
      {
        name: 'B. Prise de besoin PRIsME IA',
        description:
          "Prise de besoin ciblée Agentic AI avant l'ODS holistique : objectifs d'affaires, processus à augmenter, irritants, données disponibles, risques et valeur attendue.",
        raci: { compte: 'R', cp: 'A', dti: 'C', client: 'C' },
        assignees: { compte: 'Directeurs de compte', cp: 'Eric Durand', dti: 'Directeurs TI' },
      },
      {
        name: 'C. Refonte du contrat global BZ',
        description:
          "Regrouper les départements concernés dans un seul contrat clair : programmation, TI, cloud, cybersécurité, télécom, gestion et support. Une seule promesse, un seul périmètre, une seule responsabilité.",
        raci: { compte: 'R', cp: 'A', dti: 'C', prog: 'C', ti: 'C', cloud: 'C' },
        assignees: { compte: 'Directeurs de compte', cp: 'Eric Durand', dti: 'Directeurs TI' },
      },
      {
        name: 'D. Signature client & feu vert exécution',
        description:
          "Le client signe le contrat global. À partir de ce moment seulement, le mandat passe en exécution : prise en charge, ODS holistique, plan de livraison et déploiement agentique.",
        raci: { compte: 'R', cp: 'A', dti: 'C', client: 'C' },
        assignees: { compte: 'Directeurs de compte', cp: 'Eric Durand', dti: 'Directeurs TI' },
      },
    ],
  },
  {
    id: 'etape1-charge',
    step: 'Étape 1',
    title: 'Charge de projet',
    subtitle: 'Prise en charge après signature et coordination des travaux.',
    icon: ClipboardCheck,
    tone: {
      text: 'text-indigo-300',
      dot: 'bg-indigo-400',
      ring: 'ring-indigo-400/40',
      chip: 'bg-indigo-500/15 text-indigo-200 border-indigo-400/30',
      glow: 'bg-indigo-500/20',
    },
    composantes: [
      {
        name: 'Gestion de projet',
        description:
          "Porteur du déploiement et coordonnateur de la réalisation des travaux : cadrage, planification, suivi et arbitrage tout au long du mandat.",
        raci: { client: 'C', prog: 'R', cp: 'A/R' },
        assignees: { cp: 'Eric Durand', prog: 'Guillaume Cloutier' },
      },
    ],
  },
  {
    id: 'etape2',
    step: 'Étape 2',
    title: 'Fondations stratégiques',
    subtitle: "On prépare votre organisation avant d'introduire l'IA.",
    icon: Compass,
    tone: {
      text: 'text-violet-300',
      dot: 'bg-violet-400',
      ring: 'ring-violet-400/40',
      chip: 'bg-violet-500/15 text-violet-200 border-violet-400/30',
      glow: 'bg-violet-500/20',
    },
    composantes: [
      {
        name: 'Gouvernance IA',
        description:
          "Mise en place d'un cadre structuré : rôles, responsabilités, accès, règles d'utilisation et conformité (Loi 25). Basé sur NIST AI RMF, Microsoft Responsible AI et OpenAI Agent Governance — gestion des risques, traçabilité et supervision.",
        raci: { client: 'C', prog: 'R', cp: 'A/R' },
        assignees: { cp: 'Eric Durand' },
      },
      {
        name: 'Cartographie des données & processus',
        description:
          "Analyse des sources (CRM, ERP, fichiers) et des flux opérationnels pour identifier interactions, dépendances et opportunités d'automatisation, puis prioriser les cas d'usage pertinents.",
        raci: { client: 'C', prog: 'R', cp: 'A/R' },
        assignees: { cp: 'Eric Durand', prog: 'SA' },
      },
      {
        name: 'Architecture',
        description:
          "Validation de l'architecture agentique cible : segmentation des environnements, gestion des accès, mécanismes de protection et principes de sécurité. Recommandations de sécurité et checklist de conformité.",
        raci: { client: 'C', prog: 'R', cp: 'A/R' },
        assignees: { cp: 'Eric Durand' },
      },
      {
        name: 'Hygiène des données',
        description:
          "Validation des données et documents destinés à l'agent IA afin de garantir la qualité des informations exploitées et la fiabilité des résultats, avec recommandations de maintien de la qualité.",
        raci: { client: 'C', prog: 'R', cp: 'A' },
        assignees: { cp: 'Eric Durand' },
      },
    ],
  },
  {
    id: 'etape3',
    step: 'Étape 3',
    title: 'Activation & mise en opération',
    subtitle: 'On construit votre employé numérique prêt à générer de la valeur.',
    icon: Rocket,
    tone: {
      text: 'text-sky-300',
      dot: 'bg-sky-400',
      ring: 'ring-sky-400/40',
      chip: 'bg-sky-500/15 text-sky-200 border-sky-400/30',
      glow: 'bg-sky-500/20',
    },
    composantes: [
      {
        name: 'Architecture dédiée',
        description:
          "Déploiement de l'agent et de l'environnement technique dédié pour opérer de façon isolée et sécurisée (exécution, supervision, documentation des activités agentiques).",
        raci: { prog: 'R', cp: 'A/R' },
        assignees: { cp: 'Eric Durand' },
      },
      {
        name: 'Ordinateur dédié',
        description: "Achat et configuration d'un ordinateur dédié pour l'agentique IA.",
        raci: { prog: 'R', ti: 'C', cp: 'A', achat: 'R' },
        assignees: { cp: 'Eric Durand', achat: 'Équipe d\'achat BZ' },
      },
      {
        name: 'Environnement Google Workspace',
        description: "Installation et configuration d'un environnement Google Workspace Starter isolé.",
        raci: { prog: 'R', cp: 'A' },
        assignees: { cp: 'Eric Durand' },
      },
      {
        name: 'Avis de confidentialité',
        description:
          "Implantation des avis de confidentialité organisationnels encadrant les situations de mauvais destinataire ou de fuite involontaire liées à l'usage d'un agent IA.",
        raci: { prog: 'R', client: 'C', cp: 'A' },
        assignees: { cp: 'Eric Durand' },
      },
      {
        name: 'Outil conversationnel (Télégram)',
        description: "Installation et configuration de l'outil conversationnel Télégram (ordinateur et mobile).",
        raci: { prog: 'R', cp: 'A' },
        assignees: { cp: 'Eric Durand' },
      },
      {
        name: "Hébergement de l'ordinateur dédié",
        description: "Installation de l'hébergement de l'ordinateur dédié dans notre centre de serveur de façon sécurisée.",
        raci: { prog: 'C', cloud: 'R' },
        assignees: { prog: 'Équipe programmation', cloud: 'Équipe du cloud' },
      },
      {
        name: 'Sous-domaine',
        description: 'Configuration du sous-domaine dédié à l’agent IA.',
        raci: { ti: 'R', cloud: 'C' },
      },
      {
        name: 'Base de connaissances (RAG)',
        description:
          "Mise en place d'un RAG simple permettant à l'IA de consulter les documents fournis et de répondre en s'appuyant sur cette base (RAG complexe exclu).",
        raci: { prog: 'R', client: 'C', cp: 'A' },
        assignees: { cp: 'Eric Durand' },
      },
      {
        name: 'Choix du LLM',
        description:
          "Sélection et installation d'un modèle (LLM) selon performance, coût, sécurité et confidentialité, ajustable par cas d'usage. Respect des principes de Responsible AI.",
        raci: { prog: 'R', cp: 'A' },
        assignees: { cp: 'Eric Durand' },
      },
      {
        name: 'Connexions API & BD',
        description:
          "Intégration des systèmes existants via API sécurisées et accès contrôlés aux bases de données, pour interagir avec les outils métiers et documentations.",
        raci: { prog: 'R', client: 'C' },
      },
      {
        name: "Capacité de l'agent (Skills)",
        description:
          "Conception et intégration des capacités spécifiques de l'agent (répondre, analyser, automatiser, communiquer) selon les besoins métiers.",
        raci: { prog: 'R', cp: 'C' },
      },
      {
        name: 'Tests & robustesse',
        description:
          "Tests fonctionnels, de robustesse et intégration de la sécurité pour assurer la stabilité de l'agent et valider la mise en production (standards OpenAI et NIST).",
        raci: { prog: 'R', cp: 'C' },
      },
      {
        name: "Support initial de l'utilisateur",
        description: "Accompagnement des utilisateurs lors du démarrage pour faciliter la prise en main de l'agent.",
        raci: { prog: 'R', client: 'I' },
      },
      {
        name: 'Gestion des jetons (tokens)',
        description:
          "Modèle OAuth (OpenAI Codex Pro / GPT-5.4) par défaut, puis mécanismes de suivi, d'optimisation et de contrôle de la consommation pour gérer les coûts et éviter les dérives.",
        raci: { prog: 'R' },
      },
      {
        name: 'Tableau de bord de pilotage',
        description:
          "Tableau de bord de suivi pour la traçabilité des actions, le monitoring des performances, des accès et des interactions (exigences NIST et OpenAI).",
        raci: { prog: 'R' },
      },
      {
        name: 'Sauvegarde & récupération',
        description:
          "Implantation de mécanismes de sauvegarde et de récupération de l'environnement de l'agent (données, configurations, mémoire) pour assurer la continuité.",
        raci: { ti: 'R', prog: 'C' },
      },
    ],
  },
  {
    id: 'etape4',
    step: 'Étape 4',
    title: 'Supports en opération',
    subtitle: 'Accéder aux supports durant vos opérations de la plateforme.',
    icon: LifeBuoy,
    tone: {
      text: 'text-emerald-300',
      dot: 'bg-emerald-400',
      ring: 'ring-emerald-400/40',
      chip: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/30',
      glow: 'bg-emerald-500/20',
    },
    composantes: [
      {
        name: "Conseils gouvernance IA de l'agent",
        description:
          "Accompagnement sur les règles, accès, politiques et conformité pour suivre l'évolution des usages et des exigences réglementaires (gestion continue des risques — NIST, Microsoft).",
        raci: { prog: 'R', client: 'C' },
      },
      {
        name: "Conseils maintien de l'hygiène des données",
        description:
          "Conseils périodiques pour valider les données et documents utilisés par l'agent IA et garantir la qualité des informations exploitées.",
        raci: { prog: 'R', client: 'C' },
      },
      {
        name: 'Accès au tableau de bord de pilotage',
        description:
          "Accès en continu au tableau de bord : traçabilité des actions, monitoring des performances, des accès et des interactions (Vault, ERP bot).",
        raci: { prog: 'R', client: 'I' },
      },
      {
        name: "Support agent à l'utilisateur",
        description:
          "Accompagnement des utilisateurs dans l'usage, la compréhension, l'ajout d'habiletés (Skills) et l'exploitation des agents — configuration simple, hors incidents techniques.",
        raci: { prog: 'R', client: 'C' },
      },
      {
        name: 'Support agent — intégrations API / BD',
        description:
          "Intégration de nouveaux systèmes ou sources de données pour élargir le périmètre d'action de l'agent.",
        raci: { prog: 'R', client: 'C' },
      },
      {
        name: 'Support agent — optimisation des tokens',
        description:
          "Accompagnement et recommandations pour optimiser la consommation des modèles IA, contrôler les coûts et éviter les dérives.",
        raci: { prog: 'R', client: 'C' },
      },
      {
        name: 'Support TI — infra & architecture',
        description:
          "Gestion technique de l'environnement (poste dédié, M365/Google, RAG, communication, sécurité, accès, mises à jour) et support de l'infrastructure pour la stabilité et la disponibilité.",
        raci: { ti: 'R', prog: 'C' },
      },
      {
        name: 'Support TI — sauvegarde & récupération',
        description:
          "Support technique des mécanismes de sauvegarde et de récupération de l'environnement (données, configurations, mémoire, RAG, Google space) pour la continuité des opérations.",
        raci: { ti: 'R', prog: 'C' },
      },
    ],
  },
];

const ETAPE0_FLOW = [
  { marker: 'A', title: 'Qualification client', owner: 'A · Eric Durand' },
  { marker: 'B', title: 'Prise de besoin PRIsME IA', owner: 'R · Directeurs de compte' },
  { marker: 'C', title: 'Contrat global BZ', owner: 'C · Directeurs TI' },
  { marker: 'D', title: 'Signature client', owner: 'Go · exécution du contrat' },
];

const CLIENT_QUALIFICATION = [
  { title: 'True Friends', desc: 'Fort potentiel + forte relation. On priorise et on développe.', tone: 'border-emerald-400/35 bg-emerald-500/10 text-emerald-100' },
  { title: 'Butterflies', desc: 'Fort potentiel + relation fragile. On cadre vite avant d’investir.', tone: 'border-sky-400/35 bg-sky-500/10 text-sky-100' },
  { title: 'Barnacles', desc: 'Faible potentiel + forte relation. On sert bien sans surinvestir.', tone: 'border-amber-400/35 bg-amber-500/10 text-amber-100' },
  { title: 'Strangers', desc: 'Faible potentiel + relation faible. On limite ou on refuse poliment.', tone: 'border-slate-400/35 bg-slate-500/10 text-slate-100' },
];

function RaciChip({ code }: { code?: RaciCode }) {
  if (!code) {
    return <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-white/5 bg-white/[0.02] text-[10px] text-slate-600">·</span>;
  }
  return (
    <span
      className={`inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-lg px-1.5 text-[11px] font-black shadow-md ${RACI_BADGE[code]}`}
    >
      {code}
    </span>
  );
}

type OperationnelStep = {
  num: number;
  title: string;
  description: string;
  tag?: string;
  icon: typeof Target;
  accent: string;
  ring: string;
  chip: string;
  loop?: boolean;
};

type VuePillar = {
  title: string;
  description: string;
  icon: typeof Brain;
  accent: string;
  ring: string;
  chip: string;
  favorite?: boolean;
};

const VUE_PILLARS: VuePillar[] = [
  {
    title: "L'urgence est réelle",
    description:
      "L'IA s'intègre partout, année après année. Si BZ n'agit pas maintenant, notre valeur et notre pertinence s'érodent — pas demain, mais progressivement, client après client.",
    icon: AlertTriangle,
    accent: 'text-rose-300',
    ring: 'ring-rose-400/40',
    chip: 'bg-rose-500/15 border-rose-400/30',
  },
  {
    title: 'Une conviction partagée',
    description:
      "Chez BZ comme chez nos clients, le constat est le même : avec l'IA bien gouvernée, on peut en faire plus — avec moins de friction, moins de répétition, plus d'impact.",
    icon: Brain,
    accent: 'text-violet-300',
    ring: 'ring-violet-400/40',
    chip: 'bg-violet-500/15 border-violet-400/30',
  },
  {
    title: 'Le pari de Dany',
    description:
      "Dany mise sur l'évolution de BZ : déployer l'agentique chez l'ensemble de notre clientèle. Ce n'est pas un projet pilote — c'est une stratégie de revenus récurrents et de différenciation sur le long terme.",
    icon: Target,
    accent: 'text-amber-300',
    ring: 'ring-amber-400/40',
    chip: 'bg-amber-500/15 border-amber-400/30',
  },
  {
    title: 'Mobiliser 100 % de BZ',
    description:
      "Réussir exige que toute l'organisation se remette en question : qu'est-ce qu'on peut livrer avec la même exigence de qualité, mais plus vite, plus intelligemment, plus souvent?",
    icon: Building2,
    accent: 'text-sky-300',
    ring: 'ring-sky-400/40',
    chip: 'bg-sky-500/15 border-sky-400/30',
    favorite: false,
  },
  {
    title: 'Des gains de temps massifs',
    description:
      "Si chaque équipe joue le jeu, le gain de temps devient cumulatif — et ce temps libéré n'est pas du « surplus » : c'est le carburant de notre prochaine étape de croissance.",
    icon: Zap,
    accent: 'text-emerald-300',
    ring: 'ring-emerald-400/40',
    chip: 'bg-emerald-500/15 border-emerald-400/30',
    favorite: false,
  },
  {
    title: 'Plus de valeur, sans surcoût',
    description:
      "Avec ce temps récupéré, on peut offrir une valeur ajoutée tangible au client — souvent sans lui facturer un coût additionnel. C'est ainsi que l'agentique devient un avantage compétitif partagé.",
    icon: Gift,
    accent: 'text-fuchsia-300',
    ring: 'ring-fuchsia-400/40',
    chip: 'bg-fuchsia-500/15 border-fuchsia-400/30',
    favorite: true,
  },
];

type VueChainLink = {
  label: string;
  sub: string;
  title: string;
  summary: string;
  bullets: string[];
  impact: string;
};

const VUE_CHAIN: VueChainLink[] = [
  {
    label: 'On aligne BZ',
    sub: 'Même cap, mêmes réflexes',
    title: 'Mobilisation BZ',
    summary:
      "Tout le monde chez BZ doit comprendre pourquoi on vire IA, ce qu'on change dans nos façons de livrer et comment chaque équipe contribue au plan.",
    bullets: [
      'Éric, Dany et les gestionnaires donnent le cap.',
      'Chaque équipe identifie ses tâches répétitives et ses irritants.',
      'On garde la qualité BZ, mais on accélère l’exécution.',
    ],
    impact: "Sans mobilisation interne, l'IA reste un projet. Avec mobilisation, ça devient une nouvelle façon de travailler.",
  },
  {
    label: 'On libère du temps',
    sub: 'Moins de répétitif',
    title: 'Gains de temps',
    summary:
      "On utilise l'IA pour enlever les tâches qui consomment du temps sans créer assez de valeur : rédaction, analyse, suivi, préparation, recherche et orchestration.",
    bullets: [
      'Les employés gardent le jugement et la relation humaine.',
      'Les agents prennent le répétitif, le structuré et le prévisible.',
      'Le temps récupéré devient une capacité réelle, pas du temps perdu.',
    ],
    impact: 'Le gain devient visible quand il est répété dans toutes les équipes, semaine après semaine.',
  },
  {
    label: 'On réinvestit client',
    sub: 'Plus de valeur, même budget',
    title: 'Plus de valeur, sans surcoût',
    summary:
      "Le temps récupéré ne sert pas seulement à aller plus vite. Il sert à donner plus au client : meilleure analyse, meilleur suivi, meilleurs outils et plus de proactivité.",
    bullets: [
      'Le client ressent plus de valeur sans voir une explosion de coûts.',
      'BZ devient plus difficile à remplacer parce que la livraison est meilleure.',
      'On bâtit la confiance avant de chercher l’upsell.',
    ],
    impact: "C'est le coup de cœur : le client gagne, BZ gagne, et la relation devient plus forte.",
  },
  {
    label: 'On solidifie BZ',
    sub: 'Revenus durables',
    title: 'Revenus durables',
    summary:
      "Quand BZ livre plus de valeur avec une meilleure maîtrise technologique, les clients restent, les mandats s'élargissent naturellement et les revenus deviennent plus stables.",
    bullets: [
      'On protège les revenus actuels par une meilleure valeur perçue.',
      'On ouvre la porte aux agents chez plusieurs clients.',
      'On grandit par confiance durable, pas par pression commerciale.',
    ],
    impact: "Le but n'est pas de vendre plus vite. Le but est de devenir essentiel plus longtemps.",
  },
];

function VueEnsembleTab() {
  const [selectedChain, setSelectedChain] = useState<VueChainLink | null>(null);

  return (
    <div className="space-y-8">
      <div className="relative overflow-hidden rounded-2xl border border-indigo-400/25 bg-linear-to-br from-indigo-950/90 via-slate-900 to-violet-950/80 p-6 md:p-8">
        <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-violet-500/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="relative">
          <div className="mb-4 flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5 text-indigo-300" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-200/90">
              Vue d&apos;ensemble
            </span>
          </div>
          <h2 className="text-xl font-black leading-tight text-white md:text-3xl">
            L&apos;agentique n&apos;est pas une option — c&apos;est le plan d&apos;action 2026 de BZ
          </h2>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-cyan-300/20 bg-cyan-500/10 p-4">
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-cyan-200">Client</p>
              <p className="text-sm leading-7 text-slate-200 md:text-base">
                Le virage Agentic AI doit rendre nos clients plus rapides, plus rentables et plus difficiles à battre.
                BZ entre dans leur réalité, trouve ce qui ralentit leur croissance et déploie des agents utiles,
                mesurés et gouvernés. Le client doit gagner en vitesse, en marge et en capacité sans perdre le
                contrôle ni se faire imposer un projet hors budget.
              </p>
            </div>
            <div className="rounded-2xl border border-indigo-300/20 bg-indigo-500/10 p-4">
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-indigo-200">BZ</p>
              <p className="text-sm leading-7 text-slate-200 md:text-base">
                Pour BZ, attendre est le vrai risque. Si nous restons un fournisseur TI classique, d&apos;autres livreront
                plus vite et moins cher. On industrialise BZ maintenant pour protéger nos revenus, nos emplois et notre
                rôle de référence technologique (IA). Notre avantage doit être clair : des humains de confiance qui maîtrisent mieux
                l&apos;IA, l&apos;orchestration et la gouvernance que le marché.
              </p>
            </div>
          </div>
        </div>
      </div>

      <blockquote className="relative overflow-hidden rounded-2xl border border-amber-400/20 bg-amber-950/30 px-5 py-5 md:px-7 md:py-6">
        <div className="pointer-events-none absolute right-4 top-4 text-6xl font-serif leading-none text-amber-500/15">
          &ldquo;
        </div>
        <p className="relative text-sm font-semibold uppercase tracking-wide text-amber-200/80">
          Vision stratégique · Dany
        </p>
        <p className="relative mt-2 text-base font-medium leading-7 text-amber-50/95 md:text-lg">
          L&apos;IA redéfinit déjà les règles du jeu. Notre ambition n&apos;est pas de suivre cette transformation, mais de la
          diriger. Nous voulons faire de BZ une organisation augmentée par l&apos;IA, où chaque processus, chaque décision et
          chaque service bénéficie de l&apos;intelligence artificielle. En appliquant à nous-mêmes les meilleures pratiques
          que nous recommandons à nos clients, nous devenons la preuve vivante de ce qu&apos;il est possible d&apos;accomplir.
        </p>
      </blockquote>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {VUE_PILLARS.map((pillar, index) => {
          const PillarIcon = pillar.icon;
          return (
            <motion.article
              key={pillar.title}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(index * 0.05, 0.35) }}
              className={`relative overflow-hidden rounded-2xl border p-4 transition ${
                pillar.favorite
                  ? 'border-fuchsia-300/40 bg-fuchsia-950/30 shadow-lg shadow-fuchsia-500/10 hover:border-fuchsia-200/60 hover:bg-fuchsia-950/40'
                  : 'border-white/10 bg-slate-900/50 hover:border-white/20 hover:bg-slate-900/70'
              }`}
            >
              {pillar.favorite && (
                <>
                  <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-fuchsia-400/20 blur-2xl" />
                  <div className="absolute right-3 top-3 flex items-center gap-2 rounded-full border border-amber-300/50 bg-slate-950/80 py-1 pl-1 pr-2.5 text-[10px] font-black uppercase tracking-[0.12em] text-amber-100 shadow-lg shadow-amber-500/10 backdrop-blur">
                    <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-amber-200/60 bg-amber-200/10 shadow-md shadow-amber-500/20">
                      <img src="/DB.png" alt="Dany Bourassa" className="h-7 w-7 rounded-full object-cover" />
                      <Heart className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 fill-fuchsia-300 text-fuchsia-300 drop-shadow" />
                    </span>
                    <span>Coup de cœur Dany</span>
                    <Sparkles className="h-3.5 w-3.5 text-fuchsia-200" />
                  </div>
                </>
              )}
              <div
                className={`mb-3 flex h-11 w-11 items-center justify-center rounded-xl border ring-1 ${pillar.chip} ${pillar.ring}`}
              >
                <PillarIcon className={`h-5 w-5 ${pillar.accent}`} />
              </div>
              <h3 className="text-sm font-black text-white md:text-base">{pillar.title}</h3>
              <p className="mt-2 text-[13px] leading-6 text-slate-300">{pillar.description}</p>
            </motion.article>
          );
        })}
      </div>

      <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-5 md:p-6">
        <p className="mb-4 text-center text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          La chaîne de valeur BZ
        </p>
        <div className="flex flex-nowrap items-center gap-1.5 sm:gap-2">
          {VUE_CHAIN.map((link, i) => (
            <Fragment key={link.label}>
              <button
                type="button"
                onClick={() => setSelectedChain(link)}
                className="min-w-0 flex-1 rounded-xl border border-indigo-400/25 bg-indigo-500/10 px-2 py-2.5 text-center transition hover:border-indigo-200/60 hover:bg-indigo-500/20 focus:outline-none focus:ring-2 focus:ring-indigo-300/60 sm:px-3 sm:py-3"
              >
                <p className="text-xs font-black leading-tight text-white sm:text-sm">{link.label}</p>
                <p className="mt-0.5 text-[10px] leading-tight text-indigo-200/80 sm:text-[11px]">{link.sub}</p>
                <p className="mt-1 text-[9px] font-bold uppercase tracking-[0.14em] text-indigo-300/70">Détail</p>
              </button>
              {i < VUE_CHAIN.length - 1 && (
                <ArrowRight className="h-4 w-4 shrink-0 text-indigo-400/60 sm:h-5 sm:w-5" aria-hidden />
              )}
            </Fragment>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selectedChain && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedChain(null)}
          >
            <motion.div
              className="w-full max-w-xl overflow-hidden rounded-2xl border border-indigo-300/30 bg-slate-950 shadow-2xl shadow-indigo-950/60"
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="relative border-b border-white/10 bg-linear-to-br from-indigo-950 via-slate-950 to-fuchsia-950 px-5 py-5">
                <button
                  type="button"
                  onClick={() => setSelectedChain(null)}
                  className="absolute right-3 top-3 rounded-full border border-white/10 bg-white/5 p-2 text-slate-300 transition hover:bg-white/10 hover:text-white"
                  aria-label="Fermer"
                >
                  <X className="h-4 w-4" />
                </button>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-indigo-200/80">
                  Chaîne de valeur BZ
                </p>
                <h3 className="mt-2 pr-10 text-2xl font-black text-white">{selectedChain.title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-300">{selectedChain.summary}</p>
              </div>
              <div className="space-y-4 px-5 py-5">
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="mb-3 text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                    En clair pour Éric + Dany
                  </p>
                  <ul className="space-y-2 text-sm leading-6 text-slate-200">
                    {selectedChain.bullets.map((bullet) => (
                      <li key={bullet} className="flex gap-2">
                        <Sparkles className="mt-1 h-4 w-4 shrink-0 text-fuchsia-300" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="rounded-xl border border-amber-300/25 bg-amber-300/10 px-4 py-3 text-sm font-semibold leading-6 text-amber-50">
                  {selectedChain.impact}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="rounded-xl border border-indigo-500/20 bg-indigo-500/10 px-4 py-3 text-center text-xs leading-6 text-slate-400">
        Les onglets <strong className="text-indigo-200">RACI</strong> et{' '}
        <strong className="text-indigo-200">Plan de Match Opérationnel</strong> traduisent cette vision en
        responsabilités concrètes et en séquence de déploiement — objectif :{' '}
        <strong className="text-indigo-200">clients True Friends + Butterflies avec agent d&apos;ici le 15 septembre</strong>.
      </p>
    </div>
  );
}

const OPERATIONNEL_STEPS: OperationnelStep[] = [
  {
    num: 1,
    title: 'Sélection des clients True Friends + Butterflies',
    description:
      "Identifier d'abord les True Friends et les Butterflies : les comptes où le potentiel d'impact est réel. On ne pousse pas l'offre agentique partout; on cible les clients qui méritent l'investissement et peuvent devenir des vitrines de valeur.",
    tag: 'Ciblage',
    icon: Filter,
    accent: 'text-violet-300',
    ring: 'ring-violet-400/40',
    chip: 'bg-violet-500/20 border-violet-400/30',
  },
  {
    num: 2,
    title: 'Première rencontre déclenchée',
    description:
      "Remettre la liste ciblée à l'équipe des ventes : chaque client reçoit une prise de contact structurée pour présenter la nouvelle valeur agentique et créer l'élan.",
    tag: 'Ventes',
    icon: Handshake,
    accent: 'text-sky-300',
    ring: 'ring-sky-400/40',
    chip: 'bg-sky-500/20 border-sky-400/30',
  },
  {
    num: 3,
    title: 'Évaluation rapide avec Eric Durand',
    description:
      "Passer en revue, client par client, ce qui peut raisonnablement être inclus dans le déploiement agentique : périmètre, risques, délais et promesse livrable avant d'aller plus loin.",
    tag: 'Programmation',
    icon: UserCheck,
    accent: 'text-indigo-300',
    ring: 'ring-indigo-400/40',
    chip: 'bg-indigo-500/20 border-indigo-400/30',
  },
  {
    num: 4,
    title: 'Étape 0 — signature et cadrage PRIsME IA',
    description:
      "Avant l'exécution : qualification True Friends / Butterflies / Barnacles / Strangers, prise de besoin PRIsME IA, refonte d'un contrat global BZ avec tous les départements concernés, puis signature client. Après ce feu vert seulement, on démarre la prise en charge et la livraison.",
    tag: 'Contrat',
    icon: FileSignature,
    accent: 'text-emerald-300',
    ring: 'ring-emerald-400/40',
    chip: 'bg-emerald-500/20 border-emerald-400/30',
  },
  {
    num: 5,
    title: 'Prise de besoin / ODS holistique — sous 2 semaines',
    description:
      "Dans les 14 jours suivant la signature : atelier de besoins holistique avec le client pour figer les cas d'usage, les attentes, les départements touchés et le plan de déploiement concret. On établit en parallèle un plan de match sur 2 ans pour suivre la progression, structurer les jalons et assurer la qualité tout au long du mandat.",
    tag: 'Délai · 2 sem.',
    icon: ListChecks,
    accent: 'text-amber-300',
    ring: 'ring-amber-400/40',
    chip: 'bg-amber-500/20 border-amber-400/30',
  },
  {
    num: 6,
    title: 'Commande & installation de l\'agent',
    description:
      "Passer à l'action : commande, provisionnement et implantation de l'agent aux côtés du client — la promesse commerciale devient une capacité opérationnelle réelle.",
    tag: 'Déploiement',
    icon: Rocket,
    accent: 'text-rose-300',
    ring: 'ring-rose-400/40',
    chip: 'bg-rose-500/20 border-rose-400/30',
  },
  {
    num: 7,
    title: 'Optimisation du contrat client',
    description:
      "Revoir le contrat à la lumière du déploiement réel : aligner périmètre, valeur perçue et rentabilité pour que chaque mandat agentique soit durable et scalable.",
    tag: 'Valeur',
    icon: TrendingUp,
    accent: 'text-cyan-300',
    ring: 'ring-cyan-400/40',
    chip: 'bg-cyan-500/20 border-cyan-400/30',
  },
  {
    num: 8,
    title: 'Suivi opérationnel & planification',
    description:
      "Tenir des points réguliers avec le client : adoption, santé de l'agent, irritants et prochaines priorités — pour que l'agent reste utile, utilisé et évolutif.",
    tag: 'Relation',
    icon: Activity,
    accent: 'text-teal-300',
    ring: 'ring-teal-400/40',
    chip: 'bg-teal-500/20 border-teal-400/30',
  },
  {
    num: 9,
    title: 'Agents additionnels chez le client',
    description:
      "Élargir le portefeuille d'agents sur le même compte : nouveaux cas d'usage, nouvelles équipes, nouvelle valeur — sans repartir de zéro.",
    tag: 'Croissance',
    icon: Layers,
    accent: 'text-fuchsia-300',
    ring: 'ring-fuchsia-400/40',
    chip: 'bg-fuchsia-500/20 border-fuchsia-400/30',
  },
  {
    num: 10,
    title: 'Boucle continue 8 → 9',
    description:
      "Répéter le suivi opérationnel et l'ajout d'agents tant qu'il reste de la marge de valeur à capturer — le plan de match devient un moteur de revenus récurrents.",
    tag: 'Boucle',
    icon: RefreshCw,
    accent: 'text-orange-300',
    ring: 'ring-orange-400/40',
    chip: 'bg-orange-500/20 border-orange-400/30',
    loop: true,
  },
];

function PlanOperationnelTab() {
  return (
    <div className="space-y-8">
      <div className="relative overflow-hidden rounded-2xl border border-amber-400/25 bg-linear-to-br from-amber-950/80 via-slate-900 to-indigo-950/80 p-6 md:p-8">
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-12 -left-12 h-40 w-40 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1">
            <div className="mb-3 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-300" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-200/90">
                Plan de match opérationnel
              </span>
            </div>
            <h2 className="text-xl font-black leading-tight text-white md:text-2xl">
              Accélérer l&apos;implantation d&apos;agents chez les bons clients
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-200 md:text-base">
              Parcours commercial et de déploiement conçu pour transformer rapidement l&apos;offre agentique en
              réalité terrain : de la qualification True Friends / Butterflies jusqu&apos;à la croissance récurrente,
              en passant par Eric Durand comme filtre qualité et la signature d&apos;ententes solides.
            </p>
          </div>
          <div className="shrink-0 rounded-2xl border border-amber-300/30 bg-black/30 px-6 py-5 text-center backdrop-blur-sm">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-amber-500/20 ring-1 ring-amber-400/40">
              <Target className="h-6 w-6 text-amber-300" />
            </div>
            <p className="text-4xl font-black tabular-nums text-white">TF+B</p>
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-200">clients qualifiés</p>
            <div className="mt-3 flex items-center justify-center gap-1.5 text-sm font-bold text-white">
              <Calendar className="h-4 w-4 text-amber-300" />
              Objectif · 15 septembre
            </div>
          </div>
        </div>
      </div>

      <div className="relative">
        <div
          className="absolute left-[1.35rem] top-4 bottom-4 hidden w-px bg-linear-to-b from-violet-500/50 via-indigo-500/30 to-orange-500/50 md:block"
          aria-hidden
        />
        <ol className="space-y-4">
          {OPERATIONNEL_STEPS.map((step, index) => {
            const StepIcon = step.icon;
            return (
              <motion.li
                key={step.num}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: Math.min(index * 0.04, 0.4) }}
                className="relative md:pl-14"
              >
                <div
                  className={`absolute left-0 top-5 z-10 hidden h-11 w-11 items-center justify-center rounded-xl border text-sm font-black md:flex ${step.chip} ${step.accent}`}
                >
                  {step.num}
                </div>
                <article
                  className={`group rounded-2xl border bg-slate-900/50 p-4 transition hover:bg-slate-900/80 md:p-5 ${
                    step.loop
                      ? 'border-orange-400/30 ring-1 ring-orange-500/20'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                    <div
                      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border ring-1 md:hidden ${step.chip} ${step.ring}`}
                    >
                      <StepIcon className={`h-6 w-6 ${step.accent}`} />
                    </div>
                    <div
                      className={`hidden h-12 w-12 shrink-0 items-center justify-center rounded-xl border ring-1 sm:flex ${step.chip} ${step.ring}`}
                    >
                      <StepIcon className={`h-6 w-6 ${step.accent}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1.5 flex flex-wrap items-center gap-2">
                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 md:hidden">
                          Étape {step.num}
                        </span>
                        {step.tag && (
                          <span
                            className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${step.chip} ${step.accent}`}
                          >
                            {step.tag}
                          </span>
                        )}
                        {step.loop && (
                          <span className="rounded-full border border-orange-400/40 bg-orange-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-orange-200">
                            Répéter
                          </span>
                        )}
                      </div>
                      <h3 className="text-base font-black text-white md:text-lg">{step.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-slate-300">{step.description}</p>
                    </div>
                  </div>
                </article>
              </motion.li>
            );
          })}
        </ol>
      </div>

      <p className="rounded-xl border border-white/10 bg-indigo-500/10 px-4 py-3 text-center text-xs leading-6 text-slate-400">
        Ce parcours complète la matrice RACI technique : ici, l&apos;accent est mis sur la{' '}
        <strong className="text-indigo-200">vélocité commerciale</strong> et l&apos;{' '}
        <strong className="text-indigo-200">industrialisation</strong> du déploiement agentique chez BZ.
      </p>
    </div>
  );
}

function VenteTab() {
  const reponsesYannick = [
    {
      title: 'Vision claire de l’approche',
      status: 'Répondu',
      description:
        "La card explique pourquoi BZ doit entrer l’agentique chez ses clients : plus de vitesse, plus de marge, plus de contrôle et une meilleure gouvernance.",
    },
    {
      title: 'Structure des volets livrés',
      status: 'Répondu partiellement',
      description:
        'Les fondations stratégiques, le déploiement agentique, les supports et le RACI existent déjà, mais doivent être convertis en matériel de vente simple.',
    },
    {
      title: 'Rôles et responsabilités',
      status: 'Répondu',
      description:
        'La matrice RACI donne un cadre clair. Le décideur de livraison demeure Éric Durand, avec Dany comme arbitre stratégique.',
    },
    {
      title: 'Support à la tournée client',
      status: 'À compléter',
      description:
        "Il manque le pont commercial : liste des clients, priorités, message de prise de contact, critères de qualification et prochaines actions par compte.",
    },
  ];

  const manques = [
    'Liste des 50 clients à prioriser avec justification commerciale.',
    'Nom du décideur chez chaque client et relation actuelle avec BZ.',
    'Date de renouvellement, risque de perte ou opportunité de resignature.',
    'Hypothèse de besoin IA par client : gain de temps, marge, service, gouvernance ou risque.',
    'Script de première rencontre pour Yannick/Antoine.',
    'Format simple de qualification après rencontre : besoin réel, valeur potentielle, urgence, prochain pas.',
    'Règles de décision : ce qui est inclus, ce qui exige validation Éric/Dany, ce qui devient projet sur mesure.',
  ];

  const actionsVente = [
    {
      num: '1',
      title: 'Cibler',
      owner: 'Yannick + Antoine',
      description: 'Sortir la liste des comptes prioritaires et classer les clients par potentiel, risque et timing contractuel.',
    },
    {
      num: '2',
      title: 'Ouvrir la porte',
      owner: 'Yannick + Antoine',
      description: 'Planifier les rencontres avec un message simple : BZ veut aider le client à générer plus de valeur avec l’IA, sans perdre le contrôle.',
    },
    {
      num: '3',
      title: 'Qualifier',
      owner: 'Ventes + Éric',
      description: 'Ramener les irritants réels, les processus répétitifs, les données disponibles et la valeur d’affaires possible.',
    },
    {
      num: '4',
      title: 'Décider',
      owner: 'Éric Durand + Dany',
      description: 'Valider la promesse livrable, le périmètre, la gouvernance et la capacité de BZ avant tout engagement ferme.',
    },
    {
      num: '5',
      title: 'Signer et déployer',
      owner: 'Ventes + Éric',
      description: 'Transformer l’intérêt client en entente, démarrer la prise de besoins sous deux semaines et lancer le déploiement agentique.',
    },
  ];

  return (
    <div className="space-y-8">
      <div className="relative overflow-hidden rounded-2xl border border-sky-400/25 bg-linear-to-br from-slate-950 via-sky-950/80 to-indigo-950/80 p-6 md:p-8">
        <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-fuchsia-500/15 blur-3xl" />
        <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-center">
          <div>
            <div className="mb-4 flex items-center gap-2">
              <Handshake className="h-5 w-5 text-sky-300" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-200/90">
                Onglet Vente
              </span>
            </div>
            <h2 className="text-xl font-black leading-tight text-white md:text-3xl">
              Le tableau aide les ventes, mais le moteur reste la tournée client
            </h2>
            <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-200 md:text-base">
              La demande de Yannick est pertinente pour structurer l’offre, les livrables et le RACI. Elle ne doit
              toutefois pas devenir une condition avant d’aller voir les clients. L’objectif 2026 est de faire entrer
              l’IA agentique chez les clients BZ pour créer plus de valeur, protéger les contrats et renforcer la
              relation, avec une gouvernance claire.
            </p>
          </div>
          <div className="rounded-2xl border border-emerald-300/30 bg-emerald-500/10 p-5 text-center shadow-lg shadow-emerald-950/20">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl border border-emerald-300/40 bg-emerald-400/15">
              <UserCheck className="h-6 w-6 text-emerald-200" />
            </div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-emerald-200">Décision</p>
            <p className="mt-2 text-2xl font-black text-white">Éric Durand</p>
            <p className="mt-1 text-sm font-semibold text-emerald-100">avec arbitrage Dany</p>
            <p className="mt-3 text-xs leading-5 text-slate-300">
              Les ventes ouvrent les portes. Éric/Dany valident la promesse, la capacité et le périmètre livrable.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-2xl border border-white/10 bg-slate-900/50 p-5 md:p-6">
          <div className="mb-4 flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-indigo-300" />
            <h3 className="text-lg font-black text-white">Est-ce que la card répond à Yannick?</h3>
          </div>
          <div className="space-y-3">
            {reponsesYannick.map((item) => (
              <article key={item.title} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-black text-white">{item.title}</p>
                  <span
                    className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wide ${
                      item.status === 'Répondu'
                        ? 'border-emerald-300/40 bg-emerald-500/15 text-emerald-200'
                        : item.status === 'À compléter'
                          ? 'border-amber-300/40 bg-amber-500/15 text-amber-200'
                          : 'border-sky-300/40 bg-sky-500/15 text-sky-200'
                    }`}
                  >
                    {item.status}
                  </span>
                </div>
                <p className="text-sm leading-6 text-slate-300">{item.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border border-amber-300/20 bg-amber-950/20 p-5 md:p-6">
          <div className="mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-300" />
            <h3 className="text-lg font-black text-white">Ce qui manque pour répondre complètement</h3>
          </div>
          <ul className="space-y-3">
            {manques.map((item) => (
              <li key={item} className="flex gap-3 rounded-xl border border-amber-300/15 bg-black/15 px-4 py-3">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-200" />
                <span className="text-sm leading-6 text-amber-50/90">{item}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="rounded-2xl border border-white/10 bg-slate-900/45 p-5 md:p-6">
        <div className="mb-5 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-sky-300">Plan d’action vente</p>
            <h3 className="mt-1 text-xl font-black text-white">La balle aux ventes : créer le mouvement client</h3>
          </div>
          <p className="max-w-xl text-sm leading-6 text-slate-400">
            Le RACI et le tableau produit servent d’appui. Le succès vient des rencontres, de la qualification et
            de la conversion des clients en trajectoires agentiques gouvernées.
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-5">
          {actionsVente.map((action) => (
            <article key={action.num} className="relative overflow-hidden rounded-2xl border border-sky-400/20 bg-sky-500/10 p-4">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl border border-sky-300/30 bg-sky-400/15 text-sm font-black text-sky-100">
                {action.num}
              </div>
              <h4 className="text-sm font-black text-white">{action.title}</h4>
              <p className="mt-1 text-[11px] font-bold uppercase tracking-wide text-sky-200">{action.owner}</p>
              <p className="mt-3 text-xs leading-5 text-slate-300">{action.description}</p>
            </article>
          ))}
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <section className="rounded-2xl border border-fuchsia-300/20 bg-fuchsia-950/20 p-5 md:p-6">
          <div className="mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-fuchsia-300" />
            <h3 className="text-lg font-black text-white">Message à retenir pour Yannick</h3>
          </div>
          <blockquote className="rounded-2xl border border-fuchsia-300/20 bg-black/20 p-5 text-sm font-medium leading-7 text-fuchsia-50/95">
            Oui, on va te fournir une version claire du tableau, des volets et du RACI. Mais ce document doit
            servir à accélérer les rencontres clients, pas à retarder l’action. L’objectif 2026 est d’aller voir
            nos clients, comprendre où l’IA peut créer de la vraie valeur chez eux, et leur proposer une trajectoire
            simple, gouvernée et adaptée à leur réalité.
          </blockquote>
        </section>

        <section className="rounded-2xl border border-emerald-300/20 bg-emerald-950/20 p-5 md:p-6">
          <div className="mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-300" />
            <h3 className="text-lg font-black text-white">Ce que BZ vend vraiment</h3>
          </div>
          <p className="text-sm leading-7 text-emerald-50/90">
            Pas un upsell IA générique. BZ vend une capacité contrôlée : aider le client à faire plus d’argent,
            libérer du temps, améliorer ses opérations et garder la gouvernance. La vente doit ouvrir la porte;
            la valeur réelle se précise ensuite dans les besoins du client.
          </p>
        </section>
      </div>
    </div>
  );
}

export default function PlanDeMatchIntegrationAgenticAIModal({
  isOpen,
  onClose,
  workshopFooter,
}: PlanDeMatchIntegrationAgenticAIModalProps) {
  const [activeTab, setActiveTab] = useState<MainTabId>('vue');
  const [activePhase, setActivePhase] = useState<string>('all');

  const visiblePhases = useMemo(
    () => (activePhase === 'all' ? PHASES : PHASES.filter((p) => p.id === activePhase)),
    [activePhase]
  );

  const totalComposantes = useMemo(() => PHASES.reduce((acc, p) => acc + p.composantes.length, 0), []);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-[120] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/75 backdrop-blur-sm"
          onClick={onClose}
        />

        <Dialog.Panel data-ce-presentation={workshopFooter ? "true" : undefined} as={motion.div}
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 240, damping: 26 }}
          className="relative z-10 flex h-[94vh] w-[97vw] max-w-7xl flex-col overflow-hidden rounded-3xl border border-indigo-400/20 bg-slate-950 shadow-2xl"
        >
          <div className="relative shrink-0 border-b border-indigo-500/25 bg-linear-to-r from-slate-900 via-indigo-950 to-slate-900 px-4 py-4 md:px-6 md:py-5">
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 z-10 rounded-xl border border-white/15 bg-white/10 p-2 text-white transition hover:bg-white/20 md:right-4 md:top-4"
              aria-label="Fermer"
            >
              <X className="h-4 w-4 md:h-5 md:w-5" />
            </button>
            <div className="pr-12">
              <Dialog.Title as="h1" className="text-xl font-black tracking-tight text-white md:text-2xl">
                Plan d&apos;action 2026 virage Agentic AI client BZ
              </Dialog.Title>
              <p className="mt-1 max-w-3xl text-xs leading-5 text-slate-300 md:text-sm">
                Virage Agentic AI client BZ — vision, valeur client, matrice RACI et plan opérationnel.
              </p>
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap gap-2 overflow-x-auto border-b border-white/10 bg-slate-950/95 px-4 py-3 md:px-6">
            {MAIN_TABS.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTab(t.id)}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-xl px-3 py-2 text-xs font-semibold transition-colors md:text-sm ${
                    active
                      ? 'border border-indigo-400/40 bg-indigo-500/30 text-white'
                      : 'border border-transparent bg-white/5 text-slate-300 hover:bg-white/10'
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0 opacity-90" />
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-8" data-testid="agentic-plan-content">
          {activeTab === 'raci' && (
            <>
              <div className="relative shrink-0 border-b border-white/10 bg-slate-950 px-4 py-3 md:px-6">
                <div className="flex flex-col gap-2.5 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
                  <div className="min-w-0 shrink-0">
                    <h2 className="text-sm font-bold text-white md:text-base">Matrice RACI du déploiement</h2>
                    <p className="mt-0.5 text-xs text-slate-400">
                      {PHASES.length} étapes · {totalComposantes} composantes
                    </p>
                  </div>
                  <div className="min-w-0 flex-1 rounded-xl border border-white/10 bg-slate-900/80 px-2.5 py-2">
                    <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      Légende
                    </p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                      {RACI_LEGEND.map((l) => (
                        <div
                          key={l.code}
                          className="flex items-center gap-1.5"
                          title={`${l.title} — ${l.desc}`}
                        >
                          <RaciChip code={l.code} />
                          <span className="text-[11px] leading-tight text-slate-300">
                            <span className="font-bold text-slate-100">{l.code}</span>
                            <span className="text-slate-500"> · </span>
                            {l.title}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex shrink-0 flex-wrap gap-2 overflow-x-auto border-b border-white/10 bg-slate-950/95 px-4 py-3 md:px-6">
                <PhaseTab active={activePhase === 'all'} onClick={() => setActivePhase('all')} label="Toutes les étapes" />
                {PHASES.map((p) => (
                  <PhaseTab
                    key={p.id}
                    active={activePhase === p.id}
                    onClick={() => setActivePhase(p.id)}
                    label={`${p.step} · ${p.title}`}
                  />
                ))}
              </div>
            </>
          )}

            {activeTab === 'vue' && <VueEnsembleTab />}

            {activeTab === 'operationnel' && <PlanOperationnelTab />}

            {activeTab === 'vente' && <VenteTab />}

            {activeTab === 'raci' && (
            <div className="space-y-10">
              <section className="overflow-hidden rounded-2xl border border-emerald-400/25 bg-linear-to-br from-emerald-950/70 via-slate-900 to-indigo-950/70 p-5 md:p-6">
                <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-200/80">
                      Étape 0 · porte d&apos;entrée
                    </p>
                    <h2 className="mt-1 text-xl font-black text-white md:text-2xl">
                      Signature client avant exécution du contrat
                    </h2>
                    <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">
                      Une seule Étape 0 : elle sécurise le mandat avant la prise de besoin / ODS holistique et avant la livraison.
                    </p>
                  </div>
                  <div className="rounded-full border border-emerald-300/30 bg-emerald-500/15 px-4 py-2 text-xs font-black uppercase tracking-wide text-emerald-100">
                    Après signature → exécution
                  </div>
                </div>

                <div className="mt-5 grid gap-3 lg:grid-cols-4">
                  {ETAPE0_FLOW.map((step) => (
                    <div key={step.marker} className="rounded-xl border border-white/10 bg-slate-950/35 p-4">
                      <div className="mb-3 flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-emerald-300/45 bg-emerald-400/15 text-sm font-black text-emerald-100">
                          {step.marker}
                        </div>
                        <h3 className="text-sm font-black text-white">{step.title}</h3>
                      </div>
                      <p className="text-xs font-semibold text-emerald-100/85">{step.owner}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-5 grid gap-3 md:grid-cols-4">
                  {CLIENT_QUALIFICATION.map((q) => (
                    <div key={q.title} className={`rounded-xl border p-3 ${q.tone}`}>
                      <h3 className="text-sm font-black">{q.title}</h3>
                      <p className="mt-1 text-xs leading-5 text-slate-300">{q.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              {visiblePhases.map((phase, phaseIndex) => {
                const PhaseIcon = phase.icon;
                return (
                  <section key={phase.id}>
                    {/* Phase header */}
                    <div className="relative mb-5 overflow-hidden rounded-2xl border border-white/10 bg-slate-900/60 p-5">
                      <div className={`pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full blur-3xl ${phase.tone.glow}`} />
                      <div className="relative flex items-center gap-4">
                        <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ring-1 ${phase.tone.ring} ${phase.tone.chip} border`}>
                          <PhaseIcon className="h-7 w-7" />
                        </div>
                        <div>
                          <p className={`text-xs font-black uppercase tracking-[0.25em] ${phase.tone.text}`}>{phase.step}</p>
                          <h2 className="text-xl font-black text-white md:text-2xl">{phase.title}</h2>
                          <p className="mt-0.5 text-sm text-slate-400">{phase.subtitle}</p>
                        </div>
                      </div>
                    </div>

                    {/* Composantes */}
                    <div className="grid gap-3">
                      {phase.composantes.map((c, i) => (
                        <motion.div
                          key={c.name}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: Math.min((phaseIndex * 0.05) + i * 0.03, 0.5) }}
                          className="group grid gap-4 rounded-2xl border border-white/10 bg-slate-900/40 p-4 transition hover:border-white/20 hover:bg-slate-900/70 lg:grid-cols-[minmax(0,1fr)_auto]"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`h-2 w-2 shrink-0 rounded-full ${phase.tone.dot}`} />
                              <h3 className="text-sm font-black text-white md:text-base">{c.name}</h3>
                            </div>
                            <p className="mt-1.5 text-[13px] leading-6 text-slate-300">{c.description}</p>
                          </div>

                          {/* RACI — colonnes actives seulement */}
                          <div className="flex shrink-0 flex-wrap items-end justify-end gap-3 lg:border-l lg:border-white/10 lg:pl-4">
                            {activeRolesFor(c).map((role) => {
                              const code = c.raci[role.key];
                              const person = c.assignees?.[role.key];
                              return (
                                <div key={role.key} className="flex min-w-[72px] max-w-[92px] flex-col items-center gap-1">
                                  <RaciChip code={code} />
                                  {person ? (
                                    <span className="text-center text-[10px] font-bold leading-tight text-emerald-200">
                                      {person}
                                    </span>
                                  ) : (
                                    <span className="text-center text-[9px] font-semibold uppercase leading-tight tracking-wide text-slate-500">
                                      {role.label}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
            )}
          </div>
          {workshopFooter}
        </Dialog.Panel>
      </Dialog>
    </AnimatePresence>
  );
}

/** Alias pour compatibilité */
export function RaciDeploiementAgentiqueModal(props: PlanDeMatchIntegrationAgenticAIModalProps) {
  return <PlanDeMatchIntegrationAgenticAIModal {...props} />;
}

function PhaseTab({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 whitespace-nowrap rounded-xl px-3 py-2 text-xs font-semibold transition md:text-sm ${
        active
          ? 'border border-indigo-400/40 bg-indigo-500/30 text-white'
          : 'border border-transparent bg-white/5 text-slate-300 hover:bg-white/10'
      }`}
    >
      {label}
    </button>
  );
}
